import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import type { ForgeRemoteApi, ForgeRemoteDataSourceKind } from "../api/contracts";

const storageKey = (source: ForgeRemoteDataSourceKind) => `lani-forge-remote.${source}.activated`;

export function useActivationState(
  api: ForgeRemoteApi,
  source: ForgeRemoteDataSourceKind,
  freshActivationLink: boolean,
) {
  const queryClient = useQueryClient();
  const key = storageKey(source);
  const [activated, setActivated] = useState(() => {
    if (freshActivationLink) window.sessionStorage.removeItem(key);
    if (source === "portal") return !freshActivationLink;
    return !freshActivationLink && window.sessionStorage.getItem(key) === "true";
  });

  const forget = useCallback(() => {
    queryClient.removeQueries({ queryKey: ["forge-remote", source] });
    window.sessionStorage.removeItem(key);
    setActivated(false);
  }, [key, queryClient, source]);

  const activate = useCallback(() => {
    queryClient.removeQueries({ queryKey: ["forge-remote", source] });
    if (source === "development-fixture") window.sessionStorage.setItem(key, "true");
    setActivated(true);
  }, [key, queryClient, source]);

  const end = useCallback(async () => {
    try {
      await api.endSession();
    } finally {
      forget();
    }
  }, [api, forget]);

  return {
    activated,
    activate,
    end,
    forget,
  };
}
